import { CommonModule } from '@angular/common';
import { Component, effect, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';

import { getStatusColor } from '../../../../lib/functions';
import { MapMarker, MarkerService } from '../../../../lib/types';

@Component({
  selector: 'app-map-popup-content',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  template: `
    <div class="flex max-h-90 flex-col text-sm">
      <div
        class="service-tabs flex flex-wrap items-center gap-x-1 bg-gray-50 px-2 pt-2"
        role="tablist"
      >
        @for (service of mapMarker()?.services || []; track service.name) {
          <button
            type="button"
            role="tab"
            (click)="selectedService = service"
            class="-mb-px cursor-pointer border-b-2 px-2 pb-2 font-semibold transition-colors duration-100"
            [attr.aria-pressed]="selectedService?.name === service.name"
            [attr.aria-selected]="selectedService?.name === service.name"
            [attr.aria-label]="'Show details for ' + service.name"
            [ngClass]="
              selectedService?.name === service.name
                ? 'border-blue-700 text-blue-700'
                : 'border-gray-200 text-slate-400 hover:text-slate-600'
            "
          >
            {{ service.name }}
          </button>
        }
      </div>

      @if (selectedService; as service) {
        <div
          class="service-summary flex flex-wrap items-center gap-x-6 gap-y-1.5 border-b border-gray-200 border-l-[3px] px-3 py-3"
          [style.border-left-color]="serviceStatusColor(service.status)"
          [style.background-color]="serviceStatusTint(service.status)"
        >
          <div class="flex flex-col gap-0.5">
            <span
              class="text-[10px] font-semibold tracking-wide text-slate-400 uppercase"
              >Service Type</span
            >
            <span class="font-medium text-slate-700" matTooltip="Service type">
              {{ service.recordType }}
            </span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span
              class="text-[10px] font-semibold tracking-wide text-slate-400 uppercase"
              >Source</span
            >
            <span class="font-medium text-slate-700" matTooltip="Source">
              {{ service.source }}
            </span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span
              class="text-[10px] font-semibold tracking-wide text-slate-400 uppercase"
              >Status</span
            >
            <span
              class="flex items-center gap-1.5 font-bold"
              [style.color]="serviceStatusColor(service.status)"
              matTooltip="Status"
            >
              <span
                class="status-dot h-2.5 w-2.5 rounded-full"
                [style.background-color]="serviceStatusColor(service.status)"
              ></span>
              {{ service.status }}
            </span>
          </div>
        </div>
      }

      <div
        class="element-list flex min-h-0 flex-1 flex-col divide-y divide-gray-100 overflow-y-auto px-3"
      >
        @if (selectedService; as service) {
          @for (element of service.elements || []; track element.name) {
            <div
              class="element-row flex items-center justify-between gap-2 py-2"
            >
              <div class="min-w-0">
                <div class="truncate font-semibold text-slate-800">
                  {{ element.name }}
                </div>
                @if (element.vendor?.name) {
                  <div class="truncate text-xs text-slate-500">
                    {{ element.vendor?.name }}
                  </div>
                }
              </div>
              @if (element.recordType?.name) {
                <span class="shrink-0 text-xs text-slate-400">
                  {{ element.recordType?.name }}
                </span>
              }
            </div>
          }
        }
      </div>

      @if (selectedService; as service) {
        <div class="border-t border-gray-200 px-3 py-2.5">
          <button
            type="button"
            (click)="viewServices.emit(service.name)"
            class="flex cursor-pointer items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-800"
          >
            View service details
            <span aria-hidden="true">&rarr;</span>
          </button>
        </div>
      }
    </div>
  `,
})
export class MapPopupContentComponent {
  readonly mapMarker = input<MapMarker>();

  readonly viewServices = output<string>();

  readonly serviceStatusColor = getStatusColor;

  selectedService: MarkerService | null = null;

  constructor() {
    effect(() => {
      const value = this.mapMarker();
      if (value) {
        this.selectedService = value.services[0] || null;
      }
    });
  }

  serviceStatusTint(status: string): string {
    const hex = getStatusColor(status);
    const value = parseInt(hex.slice(1), 16);
    // eslint-disable-next-line no-bitwise
    const r = (value >> 16) & 255;
    // eslint-disable-next-line no-bitwise
    const g = (value >> 8) & 255;
    // eslint-disable-next-line no-bitwise
    const b = value & 255;
    return `rgba(${r}, ${g}, ${b}, 0.08)`;
  }
}

